import React, { useEffect, useState, useRef } from 'react';
import { X, Check, Search, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@planner/bundles/main/lib/utils';

export interface Option {
  value: string;
  label: string;
}

interface AsyncMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  onSearch: (searchTerm: string) => Promise<Option[]>;
  placeholder?: string;
  isMulti?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  minSearchLength?: number;
}

export const AsyncMultiSelect: React.FC<AsyncMultiSelectProps> = ({
  value,
  onChange,
  onSearch,
  placeholder = 'Select...',
  isMulti = true,
  disabled = false,
  id,
  className,
  minSearchLength = 2
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Load initial options when component mounts
  useEffect(() => {
    if (value.length > 0) {
      // Try to load options for selected values
      onSearch('').then(results => {
        const selected = results.filter(opt => value.includes(opt.value));
        setSelectedOptions(selected);
      }).catch(console.error);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchAsync = async () => {
      if (searchTerm.length >= minSearchLength || searchTerm === '') {
        setIsLoading(true);
        try {
          const results = await onSearch(searchTerm);
          setOptions(results);
        } catch (error) {
          console.error('Search error:', error);
          setOptions([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setOptions([]);
      }
    };

    const timeoutId = setTimeout(searchAsync, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, onSearch, minSearchLength]);

  const toggleOption = (optionValue: string) => {
    const option = options.find(opt => opt.value === optionValue);
    if (!option) return;

    if (isMulti) {
      const newValue = value.includes(optionValue)
        ? value.filter(v => v !== optionValue)
        : [...value, optionValue];
      
      // Update selected options
      if (value.includes(optionValue)) {
        setSelectedOptions(selectedOptions.filter(opt => opt.value !== optionValue));
      } else {
        setSelectedOptions([...selectedOptions, option]);
      }
      
      onChange(newValue);
    } else {
      onChange([optionValue]);
      setSelectedOptions([option]);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const removeOption = (optionValue: string) => {
    onChange(value.filter(v => v !== optionValue));
    setSelectedOptions(selectedOptions.filter(opt => opt.value !== optionValue));
  };

  const handleToggleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (isMulti) {
      return selectedOptions.map(opt => opt.label).join(', ');
    }
    return selectedOptions[0]?.label || placeholder;
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div
        id={id}
        className={cn(
          "min-h-[42px] border border-gray-300 rounded-lg px-3 py-2 cursor-pointer",
          "flex items-center justify-between",
          disabled && "opacity-50 cursor-not-allowed bg-gray-50",
          isOpen && "ring-2 ring-blue-500 border-transparent"
        )}
        onClick={handleToggleOpen}
      >
        <div className="flex items-center gap-2 flex-wrap flex-1">
          {isMulti && value.length > 0 ? (
            selectedOptions.map(option => (
              <span
                key={option.value}
                className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded"
              >
                {option.label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(option.value);
                  }}
                  className="hover:text-blue-900"
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))
          ) : (
            <span className={value.length === 0 ? "text-gray-500" : "text-gray-900"}>
              {getDisplayText()}
            </span>
          )}
        </div>
        <ChevronDown className={cn(
          "h-4 w-4 text-gray-500 transition-transform",
          isOpen && "transform rotate-180"
        )} />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
              </div>
            ) : options.length === 0 && searchTerm.length < minSearchLength && searchTerm !== '' ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                Type at least {minSearchLength} characters to search
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? `No results found for "${searchTerm}"` : 'No options available'}
              </div>
            ) : (
              options.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={cn(
                    "w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between",
                    value.includes(option.value) && "bg-blue-50"
                  )}
                  onClick={() => toggleOption(option.value)}
                >
                  <span className={value.includes(option.value) ? "font-medium" : ""}>
                    {option.label}
                  </span>
                  {value.includes(option.value) && (
                    <Check className="h-4 w-4 text-blue-600" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};